import React, { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';

import { Button } from 'shadcn/components/ui/Button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from 'shadcn/components/ui/Dialog';
import { Input } from 'shadcn/components/ui/Input';
import { Label } from 'shadcn/components/ui/Label';
import { Textarea } from 'shadcn/components/ui/Textarea';

const EMPTY_FORM = Object.freeze({
    pickupDateTime: '',
    deliveryDateTime: '',
    setTemp: '',
    carrierName: '',
    pickupSpecialInstructions: '',
    deliverySpecialInstructions: '',
});

/**
 * @param {Object} props
 * @param {boolean} props.open
 * @param {(open: boolean) => void} props.onOpenChange
 * @param {(payload: {
 *   pickupDateTime: string,
 *   deliveryDateTime: string,
 *   setTemp: string,
 *   carrierName: string,
 *   pickupSpecialInstructions: string,
 *   deliverySpecialInstructions: string
 * }) => void | Promise<void>} props.onSubmit
 * @param {boolean} [props.isSubmitting=false]
 * @param {number | string | null} [props.transferId=null]
 */
export function FCTransferBolDownloadModal({ isSubmitting = false, onOpenChange, onSubmit, open, transferId = null }) {
    const [formData, setFormData] = useState(() => ({ ...EMPTY_FORM }));
    const [validationError, setValidationError] = useState('');

    useEffect(() => {
        if (!open) {
            return;
        }
        setFormData({ ...EMPTY_FORM });
        setValidationError('');
    }, [open, transferId]);

    const transferNumberDisplay = useMemo(() => {
        if (transferId === null || transferId === undefined || transferId === '') {
            return null;
        }
        return String(transferId);
    }, [transferId]);

    const patchField = (key, value) => {
        setValidationError('');
        setFormData((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const normalizePayload = () => ({
        pickupDateTime: formData.pickupDateTime.trim(),
        deliveryDateTime: formData.deliveryDateTime.trim(),
        setTemp: formData.setTemp.trim(),
        carrierName: formData.carrierName.trim(),
        pickupSpecialInstructions: formData.pickupSpecialInstructions.trim(),
        deliverySpecialInstructions: formData.deliverySpecialInstructions.trim(),
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        const payload = normalizePayload();
        if (!payload.pickupDateTime) {
            setValidationError('Pickup date and time is required');
            return;
        }
        if (!payload.deliveryDateTime) {
            setValidationError('Delivery date and time is required');
            return;
        }
        if (!payload.setTemp) {
            setValidationError('Set temp is required');
            return;
        }
        if (!payload.carrierName) {
            setValidationError('Carrier name is required');
            return;
        }
        onSubmit?.(payload);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-h-[90vh] overflow-y-auto sm:max-w-2xl"
                onEscapeKeyDown={(e) => {
                    if (isSubmitting) {
                        e.preventDefault();
                    }
                }}
                onPointerDownOutside={(e) => {
                    if (isSubmitting) {
                        e.preventDefault();
                    }
                }}
            >
                <DialogHeader>
                    <DialogTitle>Download BOL</DialogTitle>
                    <DialogDescription>
                        Enter the transport details to generate the Bill of Lading PDF.
                        {transferNumberDisplay ? ` Transfer #${transferNumberDisplay}` : ''}
                    </DialogDescription>
                </DialogHeader>

                <form className="space-y-4" onSubmit={handleSubmit}>
                    <div className="grid gap-1.5">
                        <Label htmlFor="pickup-date-time">Pickup date and time</Label>
                        <Input
                            id="pickup-date-time"
                            aria-label="Pickup date and time"
                            placeholder="e.g. 04/26 BEFORE 1pm"
                            value={formData.pickupDateTime}
                            onChange={(e) => patchField('pickupDateTime', e.target.value)}
                        />
                    </div>

                    <div className="grid gap-1.5">
                        <Label htmlFor="pickup-special-instructions">Pickup special instructions</Label>
                        <Textarea
                            id="pickup-special-instructions"
                            aria-label="Pickup special instructions"
                            value={formData.pickupSpecialInstructions}
                            onChange={(e) => patchField('pickupSpecialInstructions', e.target.value)}
                        />
                    </div>

                    <div className="grid gap-1.5">
                        <Label htmlFor="delivery-date-time">Delivery date and time</Label>
                        <Input
                            id="delivery-date-time"
                            aria-label="Delivery date and time"
                            placeholder="e.g. Thursday AFTER 1PM"
                            value={formData.deliveryDateTime}
                            onChange={(e) => patchField('deliveryDateTime', e.target.value)}
                        />
                    </div>

                    <div className="grid gap-1.5">
                        <Label htmlFor="delivery-special-instructions">Delivery special instructions</Label>
                        <Textarea
                            id="delivery-special-instructions"
                            aria-label="Delivery special instructions"
                            value={formData.deliverySpecialInstructions}
                            onChange={(e) => patchField('deliverySpecialInstructions', e.target.value)}
                        />
                    </div>

                    <div className="grid gap-1.5 sm:grid-cols-2 sm:gap-3">
                        <div className="grid gap-1.5">
                            <Label htmlFor="set-temp">Set temp</Label>
                            <Input
                                id="set-temp"
                                aria-label="Set temp"
                                value={formData.setTemp}
                                onChange={(e) => patchField('setTemp', e.target.value)}
                            />
                        </div>
                        <div className="grid gap-1.5">
                            <Label htmlFor="carrier-name">Carrier name</Label>
                            <Input
                                id="carrier-name"
                                aria-label="Carrier name"
                                value={formData.carrierName}
                                onChange={(e) => patchField('carrierName', e.target.value)}
                            />
                        </div>
                    </div>

                    {validationError ? (
                        <p className="text-sm text-destructive" role="alert">
                            {validationError}
                        </p>
                    ) : null}

                    <DialogFooter className="gap-2 sm:gap-0">
                        <Button
                            type="button"
                            variant="outline"
                            disabled={isSubmitting}
                            onClick={() => onOpenChange?.(false)}
                        >
                            Cancel
                        </Button>
                        <Button type="submit" disabled={isSubmitting}>
                            {isSubmitting ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                                    Downloading...
                                </>
                            ) : (
                                'Download BOL'
                            )}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

FCTransferBolDownloadModal.displayName = 'FCTransferBolDownloadModal';
